import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CandidateCvUploadResponseDto, CandidateProfileDto, CandidateProfileUpdateRequestDto } from "../models/candidate-profile.model";
import { CandidateApplicationDetailDto, CandidateApplicationDto, CandidateCvResponseDto } from "../models/candidate-application.model";
import { environment } from "../../../../environments/environment";


/**
 * Candidate-portal API — pokriva /candidate-portal/* endpointe (profil,
 * prijave, CV ops). Svi pozivi traže Bearer (authInterceptor attacha);
 * 401 handle-a refreshTokenInterceptor. Auth/login pozivi (/auth/*) žive u
 * AuthApiService.
 */
@Injectable({ providedIn: 'root' })
export class CandidatePortalApiService {
    private http: HttpClient = inject(HttpClient);
    private apiUrl = environment.apiBaseUrl;

    /**
     * GET /candidate-portal/my-profile — full snapshot trenutnog candidate profila.
     * Bearer attacha authInterceptor; 401 handle-a refreshTokenInterceptor.
     */
    getMyProfile(): Observable<CandidateProfileDto> {
        return this.http.get<CandidateProfileDto>(
            `${this.apiUrl}/candidate-portal/my-profile`,
            { withCredentials: true },
        );
    }

    /**
     * PUT /candidate-portal/my-profile — update candidate profila.
     * Bearer attacha authInterceptor; 401 handle-a refreshTokenInterceptor.
     * withCredentials zbog refresh cookie-ja (konzistentno s ostalim pozivima).
     */
    updateProfile(body: CandidateProfileUpdateRequestDto): Observable<CandidateProfileDto> {
        return this.http.put<CandidateProfileDto>(
            `${this.apiUrl}/candidate-portal/my-profile`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-applications — lista svih candidate recorda
     * (prijava) trenutnog usera. Backend ne sortira; frontend sortira po
     * appliedAt desc. `id` u svakom item-u je candidate.id (Candidate tablica,
     * nije user.id) — koristi se za navigaciju na detail u koracima 8/9.
     */
    getMyApplications(): Observable<CandidateApplicationDto[]> {
        return this.http.get<CandidateApplicationDto[]>(
            `${this.apiUrl}/candidate-portal/my-applications`,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-applications/{id} — detail jedne prijave.
     * `id` je candidate.id. Vraća pun snapshot s answers, interviews, score
     * polja, CV state. Backend verificira da current user posjeduje candidate
     * record (401/403 ako pokuša tuđu prijavu).
     */
    getApplicationDetail(id: number): Observable<CandidateApplicationDetailDto> {
        return this.http.get<CandidateApplicationDetailDto>(
            `${this.apiUrl}/candidate-portal/my-applications/${id}`,
            { withCredentials: true },
        );
    }

    /**
     * POST /candidate-portal/my-applications/{candidateId}/cv — multipart upload.
     * Request param: 'file'. Vraća puni CandidateCvResponseDto s GCS signed URL-om
     * za kasniji download. 201 Created na success.
     */
    uploadCv(candidateId: number, file: File): Observable<CandidateCvResponseDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<CandidateCvResponseDto>(
            `${this.apiUrl}/candidate-portal/my-applications/${candidateId}/cv`,
            formData,
            { withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/candidates/{candidateId}/cv — briše CV blob (GCS)
     * i candidate_cv row. Idempotentno: 204 i kad CV ne postoji.
     */
    deleteCv(candidateId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/candidates/${candidateId}/cv`,
            { withCredentials: true },
        );
    }

    /**
     * POST /candidate-portal/my-profile/cv — multipart upload profilnog CV-a
     * (jedan po useru; drugi upload pregazi prethodni). Request param: 'file'.
     * Content-Type NE postavljamo ručno — FormData sam doda multipart boundary.
     * Response `fileUrl` je potpisan-i-istekljiv GCS link → ignoriramo ga;
     * za download koristi downloadProfileCv(). 400 ako fali/krivi tip/prevelik.
     */
    uploadProfileCv(file: File): Observable<CandidateCvUploadResponseDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<CandidateCvUploadResponseDto>(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            formData,
            { withCredentials: true },
        );
    }

    /**
     * GET /candidate-portal/my-profile/cv — vraća same bajtove fajla
     * (responseType: 'blob'). Filename uzimamo iz profila (cvFileName), ne iz
     * Content-Disposition (cross-origin JS ga ne čita bez expose-headera).
     * 400 ako user nema CV → zovi samo kad je hasCv true.
     */
    downloadProfileCv(): Observable<Blob> {
        return this.http.get(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            { responseType: 'blob', withCredentials: true },
        );
    }

    /**
     * DELETE /candidate-portal/my-profile/cv — briše profilni CV.
     * Idempotentno: 204 i kad CV ne postoji.
     */
    deleteProfileCv(): Observable<void> {
        return this.http.delete<void>(
            `${this.apiUrl}/candidate-portal/my-profile/cv`,
            { withCredentials: true },
        );
    }
}
